import type { BucketDocument, BucketEntity } from './dtos';

/** Normalizes a scalar for correction matching: unicode, case, whitespace. */
export function normalizeMatchValue(value: string): string {
  return value.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
}

export interface UniqueValueMatch {
  propertyId: string;
  label: string;
}

/**
 * The single candidate whose normalized value equals the corrected text.
 * Zero or several matches derive nothing: an ambiguous value (two drivers
 * sharing a city) must not pin a meaning.
 */
export function findUniqueMatch<T extends { value: string }>(
  candidates: readonly T[],
  value: string
): T | null {
  const wanted = normalizeMatchValue(value);
  if (wanted === '') return null;
  const matches = candidates.filter((candidate) => normalizeMatchValue(candidate.value) === wanted);
  return matches.length === 1 ? (matches[0] ?? null) : null;
}

/**
 * Finds the single bucket property whose value equals the corrected text, so
 * a value correction can teach the field's meaning (a HINT) without asking
 * the user to name the field.
 */
export function findUniqueValueMatch(
  document: BucketDocument,
  value: string
): UniqueValueMatch | null {
  const properties: Array<UniqueValueMatch & { value: string }> = [];
  visitEntities(document.root, (entity) => {
    for (const property of entity.properties ?? []) {
      properties.push({
        propertyId: property.id,
        label: property.displayLabel || property.semanticKey,
        value: property.value
      });
    }
  });
  const match = findUniqueMatch(properties, value);
  return match ? { propertyId: match.propertyId, label: match.label } : null;
}

/** Depth-first walk over an entity tree; the root visits at depth 0. */
export function visitEntities(
  root: BucketEntity,
  visit: (entity: BucketEntity, depth: number, parent: BucketEntity | null) => void
): void {
  const walk = (entity: BucketEntity, depth: number, parent: BucketEntity | null) => {
    visit(entity, depth, parent);
    for (const child of entity.children ?? []) walk(child, depth + 1, entity);
  };
  walk(root, 0, null);
}
